import { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { type useActiveNavLink_navItems$key } from './__generated__/useActiveNavLink_navItems.graphql';

type UseActiveNavLinkProps = {
    navItems?: useActiveNavLink_navItems$key | null;
};

const navItemsFragment = graphql`
    fragment useActiveNavLink_navItems on NavBarElement @relay(plural: true) {
        ... on NavBarList {
            items {
                href
            }
        }
        href
    }
`;

/**
 * Hook to determine the active navigation link based on the current URL.
 * Orders nav links by length to find the most precise match for the current page.
 */
export const useActiveNavLink = ({ navItems: navItemsRef }: UseActiveNavLinkProps): string => {
    const navItems = useFragment(navItemsFragment, navItemsRef);
    const currentUrl = window.location.pathname;

    return useMemo<string>(() => {
        if (!navItems) {
            return '';
        }

        const orderedNavLinks: Array<string> = [];

        for (let i = 0; i < navItems.length; i++) {
            const currentNav = navItems[i];
            if (currentNav?.items?.length) {
                orderedNavLinks.push(
                    ...currentNav.items.map(l => l?.href).filter(href => typeof href === 'string')
                );
            } else if (currentNav?.href) {
                orderedNavLinks.push(currentNav.href);
            }
        }

        orderedNavLinks.sort((a, b) => (b?.length || 0) - (a?.length || 0));
        return orderedNavLinks.find(nl => nl && currentUrl.startsWith(nl)) || '';
    }, [navItems, currentUrl]);
};
